import { DISPLAY_MAP, FEATURE_IDS_MAP } from './constants'
import { ROLES_MAP, permissions } from './assetManagementConstants'
import { useQuery } from '@apollo/client'
import { GET_LOGGED_IN_USER } from '../../graphql/query/organization'
import { IUser } from '../OrganizationDetails/types'
import { useMemo } from 'react'
import { useIsAuthenticated } from '@azure/msal-react'
import { localStorageGetItem } from '../../utils/storageUtils'
import { config } from '../../config'

const useAccessControlHook = (featureId: any) => {
  const isLoggedIn = useIsAuthenticated()
  const {
    data,
    loading: userDataLoading,
    error: userDataError,
  } = useQuery(GET_LOGGED_IN_USER, {
    skip: !isLoggedIn,
    fetchPolicy: 'cache-and-network',
  })
  const userData: IUser = data?.getLoggedInUser
  const currentClientId = localStorageGetItem('orgCode')

  const allRoles = (userData?.roles ?? []).map((role) => role.name)

  const priorityRole = useMemo(() => {
    let requiredRole
    Object.keys(ROLES_MAP).forEach((role) => {
      if (allRoles.includes(ROLES_MAP[role as keyof typeof ROLES_MAP].key)) {
        requiredRole = ROLES_MAP[role as keyof typeof ROLES_MAP].key
        return
      }
    })
    return requiredRole
  }, [allRoles])

  const hasPermissions = useMemo(() => {
    if (featureId === FEATURE_IDS_MAP.viewTaskReport) {
      return userData?.userPermissions.some((permission) => permission.name === 'view-task-report')
        ? DISPLAY_MAP.show
        : DISPLAY_MAP.hide
    }
    if (featureId === FEATURE_IDS_MAP.ingestTask) {
      const isDemoClient = userData?.applications?.some(app => 
        app.clients.some(client => client.id === currentClientId && client.name === config.bngPlanClient)
      )
      if (isDemoClient) {
        return DISPLAY_MAP.hide
      }
      return userData?.userPermissions.some(
        (permission) => permission.name === 'task-ingestion-allowed'
      )
        ? DISPLAY_MAP.show
        : DISPLAY_MAP.hide
    }
    if (featureId === FEATURE_IDS_MAP.createBNGPlan) {
      if (!config.bngPlanClient) {
        console.warn('BNG Plan client not configured in environment variables')
        return DISPLAY_MAP.hide
      }
      
      const isAllowedClient = userData?.applications?.some(app => 
        app.clients.some(client => client.id === currentClientId && client.name === config.bngPlanClient)
      )
      if (!isAllowedClient) {
        return DISPLAY_MAP.hide
      }
      return DISPLAY_MAP.show
    }
    if (featureId === FEATURE_IDS_MAP.exportShapefile) {
      return userData?.userPermissions.some(
        (permission) => permission.name === 'export-shapefile-allowed'
      )
        ? DISPLAY_MAP.show
        : DISPLAY_MAP.hide
    }
    if (featureId === FEATURE_IDS_MAP.dsAutomation) {
      // Check if DS Automation is enabled via environment variable
      if (!config.dsAutomationEnabled) {
        return DISPLAY_MAP.hide
      }
  
      const hasPermission = userData?.userPermissions?.some(
        (permission) => permission.name === 'ds-automation-allowed'
      )
      
      return hasPermission ? DISPLAY_MAP.show : DISPLAY_MAP.hide
    }
    if (featureId === FEATURE_IDS_MAP.bulkCreation) {
      // Bulk creation is available for all clients for now
      return DISPLAY_MAP.show
    }
    return priorityRole
      ? permissions?.[priorityRole]?.[featureId] ?? DISPLAY_MAP.hide
      : DISPLAY_MAP.hide
  }, [featureId, priorityRole, userData?.userPermissions, userData?.applications, currentClientId])

  return { hasPermissions, userDataLoading }
}

export default useAccessControlHook
